import * as React from "react";
import _ from "lodash";
import { formatMessage } from "lib/globalize-util";
import theme from "styles/theme";
import Toggle from "material-ui/Toggle";
import IconButton from "material-ui/IconButton";
import Sort from "components/sort-picker";
import { ViewPicker } from "components/view-picker/view-picker";
import RedineIcon from "components/icons/refine";
import { Form } from "formsy-react";
import * as Card from "components/icons/layout/card";
import * as Tile from "components/icons/layout/tile";
import * as Panel from "components/icons/layout/panel";
import * as LargeTile from "components/icons/layout/large-tile";
import { List } from "components/icons/layout";
import { List as ImmutableList } from "immutable";
import { merge, $ } from "glamor";
import debounce from "lodash/debounce";
import TextField from "components/form/text-field";
import { VIEWPORT_PHABLET, VIEWPORT_MOBILE, VIEWPORT_TABLET } from "constants/media";
import { FlatButton } from "material-ui";
import classNames from "classnames";
import { DATATEST } from "constants/integration";
import { SEARCH_PRODUCTS_MAX_LENGTH } from "constants/product";
import { getDefaultView } from "./items-utils";
import { ItemViewType, SearchSettings } from "constants/types";
import { SearchModal } from "../dialog/searchModal";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import me from "models/me";
import { WindowAnalytics } from "helpers/window-analytics";
import validateBulkSearch from "helpers/validate-bulk-search";

let styles;
const getStyles = () => {
    if (!styles) {
        styles = merge(
            {
                margin: "0 -8px ",
                padding: "8px 8px 0px",
                height: "56px",
                lineHeight: "48px",
                display: "flex",
                flexWrap: "nowrap",
            },
            $(" .spacer", {
                display: "flex",
                flexGrow: 1,
                flexShrink: 1,
                marginTop: "-2px",
                marginRight: "24px",
            }),
            $(" .bulkSearch", {
                textDecoration: "underline",
                cursor: "pointer",
                marginLeft: "16px",
                height: "40px",
                lineHeight: "40px",
            })
        );
    }
    return styles;
};

export enum Types {
    ITEM = "item",
    CATALOG = "catalog",
}

interface IAdvancedSearchField {
    key: string;
    name: string;
}

interface IProps {
    type: Types;
    onSortChange: () => void;
    onViewNotesToggle: () => void;
    viewNotes: boolean;
    view: ItemViewType;
    onViewChange: (view: string) => void;
    router: any;
    sortBy: string;
    sortOrder: string;
    viewport: string;
    filters: any;
    onFilterChange: (filters: any) => void;
    onToggleFiltersOpen: () => void;
    isFiltersOpen: boolean;
    advancedSearchKey?: IAdvancedSearchField | null;
    isSticky?: boolean;
    onStickyStateChange?: (sticky: boolean) => void;
    onSnackbarAdd?: (message: any) => void;
    scope: any;
    hasResults?: boolean;
    handleSearchTypeModel: (searchType: string) => void;
}

interface IState {
    searchTerm: string;
    advancedSearchOpen: boolean;
    advancedSearchValues: string;
    showSearchTypeModel: boolean;
    enabledSearchType: string;
    searchType: string;
}

class ItemsHeader extends React.PureComponent<IProps, IState> {
    static defaultProps = {
        viewNotes: false,
        editable: false,
        advancedSearchKey: null,
    };

    private searchField: any;

    private muiStyles = {
        iconButton: {
            marginTop: "-4px",
        },
        textField: {
            maxWidth: "512px",
            height: "44px",
            display: "block",
        },
        advancedForm: {
            maxWidth: "712px",
            display: "flex",
            flexGrow: "1",
            height: "40px",
            alignItems: "flex-start",
        },
        form: {
            maxWidth: "512px",
            display: "flex",
            flexGrow: "1",
            height: "40px",
        },
        advancedSearchField: {
            height: "40px",
            display: "block",
            borderTop: `1px solid ${theme.palette.borderColor}`,
            borderBottom: `1px solid ${theme.palette.borderColor}`,
            borderLeft: `1px solid ${theme.palette.borderColor}`,
            borderRadius: "4px 0 0 4px",
            top: "2px",
        },
        simpleSearchClearIcon: {
            top: "22px",
        },
        advancedSearchInput: {
            border: "none",
            bottom: "3px",
            height: "40px",
            paddingRight: "45px",
        },
        applySearchButton: {
            backgroundColor: theme.palette.primary1Color,
            color: theme.palette.canvasColor,
            height: "40px",
            minWidth: "140px",
            borderRadius: "0 4px 4px 0",
            top: "2px",
        },
        toggle: {
            width: "auto",
            marginRight: "16px",
            paddingTop: "8px",
        },
        toggleLabel: {
            whiteSpace: "nowrap" as "nowrap",
        },
        sort: {
            margin: "0 8px",
        },
        sticky: {
            top: "60px",
            transform: "translateZ(0px)",
            position: "fixed",
        },
        disabledButton: {
            color: theme.palette.darkerGrey,
        },
        searchIcon: {
            msTransform: "rotate(90deg)",
            transform: "rotate(90deg)",
            cursor: "pointer",
            height: "2.5em",
            marginLeft: "8px",
        },
    };

    private sortChoices = [
        ["name.asc", formatMessage("Name (A-Z)")],
        ["name.desc", formatMessage("Name (Z-A)")],
        ["style_number.asc", formatMessage("Style Number (A-Z)")],
        ["style_number.desc", formatMessage("Style Number (Z-A)")],
        ["__merchandising_order.asc", formatMessage("Merchandised")],
        ["__created_on.desc", formatMessage("Recently Added")],
    ];

    private viewChoices = [
        [ItemViewType.Panel, formatMessage("Panel"), Panel.default],
        [ItemViewType.Tile, formatMessage("Tile"), Tile.default],
        [ItemViewType.LargeTile, formatMessage("Large Tile"), LargeTile.default],
        [ItemViewType.Card, formatMessage("Card"), Card.default],
        [ItemViewType.List, formatMessage("List"), List],
    ].filter((viewChoice) => !!viewChoice);

    constructor(props) {
        super(props);
        const searchType = localStorage.getItem(SearchSettings.SearchType) || DATATEST.SEARCH_TYPE_STANDARD;
        const defaultSearchType = props.filters ? props.filters.get("__search_type") || searchType : searchType;
        const defaultSearchTerm = props.filters ? props.filters.get("__search") || "" : "";

        this.state = {
            searchTerm: defaultSearchTerm,
            advancedSearchOpen: false,
            advancedSearchValues: "",
            showSearchTypeModel: false,
            enabledSearchType: defaultSearchType,
            searchType: defaultSearchType,
        };
        this.handleSearchTypeModal = this.handleSearchTypeModal.bind(this);
        this.closeSearchTypeModal = this.closeSearchTypeModal.bind(this);
        this.showSearchModal = this.showSearchModal.bind(this);
        this.handleRadioChange = this.handleRadioChange.bind(this);
        this.logSegmentEvent = this.logSegmentEvent.bind(this);
    }

    UNSAFE_componentWillMount(): void {
        if (!_.some(this.viewChoices, { "0": this.props.view })) {
            // reset view settings in case user have incorrect view set
            this.props.onViewChange(getDefaultView());
        }
    }

    componentWillUnmount() {
        (this.handleDebouncedChange as any).cancel();
    }

    UNSAFE_componentWillReceiveProps(nextProps, nextState) {
        const isMobile =
            this.props.viewport === VIEWPORT_MOBILE ||
            this.props.viewport === VIEWPORT_PHABLET ||
            this.props.viewport === VIEWPORT_TABLET;

        if (this.props.filters !== nextProps.filters && isMobile) {
            if (document.body.classList.contains("mobileFixedFix")) {
                document.body.classList.remove("mobileFixedFix");
            }
        }

        if (this?.props?.filters?.get("__search") !== nextProps?.filters?.get("__search")) {
            this.setState({
                searchTerm: nextProps.filters.get("__search") || "",
            });
        }

        if (
            nextProps.router.location.query.advanced_search === "true" &&
            !this.state.advancedSearchOpen &&
            nextProps.advancedSearchKey &&
            nextProps.router.location.query.search_field === nextProps.advancedSearchKey.key &&
            nextProps.viewport !== VIEWPORT_MOBILE
        ) {
            let advancedSearchValues = nextProps.filters.get(nextProps.advancedSearchKey.key);
            advancedSearchValues = advancedSearchValues ? advancedSearchValues.toJS().toString() : "";
            this.setState({ advancedSearchOpen: true, advancedSearchValues });
        }

        if (nextProps.advancedSearchKey) {
            const invalidAdvancedSearchField =
                nextProps.router.location.query.search_field &&
                nextProps.router.location.query.search_field !== nextProps.advancedSearchKey.key;
            const invalidAdvSearchValue =
                nextProps.router.location.query.advanced_search &&
                nextProps.router.location.query.advanced_search !== "true";

            if (
                (nextProps.viewport === VIEWPORT_MOBILE || invalidAdvancedSearchField || invalidAdvSearchValue) &&
                this.state.advancedSearchOpen
            ) {
                // no advanced search for mobile or if params are invalid
                this.handleCloseAdvancedSearch(nextProps);
            }
        }

        const nextBrandId = window.location.pathname.split("/")[2];
        const currentBrandId = this.props.scope.brand._id;
        // ordinarily we wouldn't need this, but there's a strange bug
        // where changing brands while adv search is open causes the brand url to get
        // out of sync with scope'd brand.

        if (
            !nextProps.advancedSearchKey &&
            nextBrandId === currentBrandId &&
            (nextProps.router.location.query.search_field || nextProps.router.location.query.advanced_search)
        ) {
            // user has input advanced search params but nothing is wired up
            this.handleCloseAdvancedSearch(nextProps);
        }

        if (nextProps.isSticky !== this.props.isSticky) {
            nextProps.onStickyStateChange(nextProps.isSticky);
        }

        if (nextBrandId !== currentBrandId) {
            this.setState({ advancedSearchOpen: false, advancedSearchValues: "" });
        }
    }

    handleSearch = (searchTerm) => {
        let filters = this.props.filters;
        if (searchTerm) {
            filters = filters.set("__search", searchTerm);
            filters = filters.set("__search_type", this.state.searchType);
        } else {
            filters = filters.delete("__search");
            filters = filters.delete("__search_type");
        }
        this.props.onFilterChange(filters);
    };

    // tslint:disable-next-line: member-ordering
    private handleDebouncedChange = debounce(this.handleSearch, 400);

    handleSearchChange = (e, searchTerm) => {
        this.handleDebouncedChange(searchTerm);
        this.setState({ searchTerm });
    };

    handleOpenAdvancedSearch = () => {
        this.setState({ advancedSearchOpen: true, searchTerm: "" });
        const query = {
            ...this.props.router.location.query,
            advanced_search: true,
            search_field: this.props.advancedSearchKey.key,
        };
        delete query.filters;
        delete query.__search;
        delete query.__search_type;
        this.props.router.replace({
            pathname: this.props.router.location.pathname,
            query,
        });
    };

    handleApplySearch = () => {
        const { filters, advancedSearchKey, onSnackbarAdd } = this.props;
        const { advancedSearchValues } = this.state;

        const words = [...new Set(advancedSearchValues.split(/[ ,]+/).filter((val) => !!val))];
        const error = validateBulkSearch(words);
        if (error) {
            onSnackbarAdd({ message: error });
        } else {
            this.setState({ advancedSearchValues: words.join(", ") });
            this.props.onFilterChange(
                words.length
                    ? filters.set(advancedSearchKey.key, ImmutableList(words))
                    : filters.delete(advancedSearchKey.key)
            );
        }

        this.focus();
    };

    handleTriggerCloseAdvanceSearch = () => {
        this.handleCloseAdvancedSearch(this.props);
    };

    handleCloseAdvancedSearch = (nextProps) => {
        this.setState({ advancedSearchOpen: false, advancedSearchValues: "" });
        const router = nextProps.router ? nextProps.router : this.props.router;
        const query = {
            ...router.location.query,
        };
        delete query.filters;
        delete query.advanced_search;
        delete query.search_field;

        const nextBrandId = nextProps.scope.brand._id;
        const oldBrandId = this.props.router.params.brand;
        const updatedPath = this.props.router.location.pathname.replace(oldBrandId, nextBrandId);

        if (nextBrandId === oldBrandId) {
            router.replace({
                pathname: this.props.router.location.pathname,
                query,
            });
        }
        this.setState({ searchTerm: "" });
    };

    handleAdvancedSearchChange = (e, value) => {
        if (value === "") {
            this.focus();
        }
        this.setState({ advancedSearchValues: value });
    };

    handleSearchSubmit = () => {
        if (this.state.advancedSearchOpen) {
            this.handleApplySearch();
        }
    };

    setRef = (ref: any) => {
        this.searchField = ref;
    };

    focus() {
        if (this.searchField) {
            this.searchField.querySelector("input[type=text]").focus();
        }
    }

    handleRadioChange(value: string) {
        localStorage.setItem(SearchSettings.SearchType, value);
        this.setState({
            enabledSearchType: value,
        });
    }

    handleSearchTypeModal() {
        this.setState(
            {
                searchType: this.state.enabledSearchType,
                showSearchTypeModel: false,
            },
            function () {
                this.handleSearchChange(null, this.state.searchTerm);
            }
        );
        // Log segment event
        this.logSegmentEvent(this.state.enabledSearchType);
    }

    logSegmentEvent(selectedSearchType) {
        const { name, email, account_type } = me;
        const brand = this.props.scope.brand;
        const { type, router } = this.props;
        const segmentEventPage =
            type === "item"
                ? "Product Gallery"
                : router.location.pathname.includes("/linesheet/")
                  ? "Linesheet"
                  : "Custom List";
        WindowAnalytics.track(`${segmentEventPage} Search Type Changed`, {
            name: name,
            email: email,
            userType: account_type,
            brandId: brand._id,
            brandName: brand.name,
            enabledSearchType: selectedSearchType,
        });
    }

    closeSearchTypeModal() {
        this.setState({
            enabledSearchType: this.state.searchType,
            showSearchTypeModel: false,
        });
    }

    showSearchModal() {
        this.setState({
            showSearchTypeModel: true,
        });
    }

    render() {
        const {
            viewport,
            type,
            onSortChange,
            onViewNotesToggle,
            viewNotes,
            sortBy,
            sortOrder,
            view,
            onViewChange,
            onToggleFiltersOpen,
            isFiltersOpen,
            advancedSearchKey,
            isSticky,
            scope,
        } = this.props;

        const {
            searchTerm,
            advancedSearchOpen,
            advancedSearchValues,
            showSearchTypeModel,
            enabledSearchType: selectedSearchType,
        } = this.state;
        const brandId = scope?.brand?._id;
        const searchTypeToggle = (
            <React.Fragment>
                <div
                    className="bulkSearch"
                    data-test={DATATEST.GALLERY.SIMPLE_SEARCH_BTN}
                    onClick={this.showSearchModal}
                >
                    {formatMessage(DATATEST.SEARCH_TYPE_MODAL_DIALOG_EXPANDED_TEXT)}
                </div>
                <span style={{ lineHeight: "40px" }}>
                    {" "}
                    &nbsp; {formatMessage(DATATEST.SEARCH_TYPE_MODAL_DIALOG_EXPANDED_ENABLE_TEXT)}
                </span>
            </React.Fragment>
        );
        let placeholder = formatMessage("Search Products");
        let advancedSearchToggle = (
            <React.Fragment>
                <div
                    className="bulkSearch"
                    data-test={DATATEST.GALLERY.BULK_SEARCH_BTN}
                    onClick={this.handleOpenAdvancedSearch}
                >
                    {formatMessage("Bulk Search")}
                </div>
                {selectedSearchType === DATATEST.SEARCH_TYPE_EXPANDED ? searchTypeToggle : ""}
            </React.Fragment>
        );

        if (advancedSearchOpen) {
            placeholder = formatMessage("Exact Match Product(s) by |field|", { field: advancedSearchKey.name });
            advancedSearchToggle = (
                <React.Fragment>
                    {selectedSearchType === DATATEST.SEARCH_TYPE_EXPANDED ? searchTypeToggle : ""}
                    <div
                        className="bulkSearch"
                        data-test={DATATEST.GALLERY.SIMPLE_SEARCH_BTN}
                        onClick={this.handleTriggerCloseAdvanceSearch}
                    >
                        {formatMessage("Simple Search")}
                    </div>
                </React.Fragment>
            );
        }

        return (
            <div
                {...getStyles()}
                style={isSticky ? this.muiStyles.sticky : null}
                className={classNames({ sticky: isSticky })}
            >
                <IconButton onClick={onToggleFiltersOpen} style={this.muiStyles.iconButton}>
                    <RedineIcon
                        key="refine"
                        color={isFiltersOpen ? theme.palette.primary1Color : theme.palette.darkerGrey}
                    />
                </IconButton>
                <div className="spacer" ref={this.setRef}>
                    <Form style={advancedSearchOpen ? this.muiStyles.advancedForm : this.muiStyles.form}>
                        <TextField
                            canClear={true}
                            large={false}
                            fullWidth={true}
                            dataTest={DATATEST.GALLERY.SEARCH_INPUT}
                            name="search"
                            placeholder={placeholder}
                            maxLength={advancedSearchOpen ? undefined : SEARCH_PRODUCTS_MAX_LENGTH}
                            style={advancedSearchOpen ? this.muiStyles.advancedSearchField : this.muiStyles.textField}
                            inputStyle={advancedSearchOpen ? this.muiStyles.advancedSearchInput : null}
                            clearStyle={advancedSearchOpen ? null : this.muiStyles.simpleSearchClearIcon}
                            value={advancedSearchOpen ? advancedSearchValues : searchTerm}
                            onChange={advancedSearchOpen ? this.handleAdvancedSearchChange : this.handleSearchChange}
                            onEnter={this.handleSearchSubmit}
                        />
                        {advancedSearchOpen && (
                            <FlatButton
                                label={formatMessage("Apply Search")}
                                data-test={DATATEST.GALLERY.APPLY_SEARCH_BTN}
                                primary={true}
                                style={this.muiStyles.applySearchButton}
                                onClick={this.handleApplySearch}
                            />
                        )}
                    </Form>
                    <TuneOutlinedIcon
                        data-test={DATATEST.SEARCH_SETTINGS.OPEN_MODAL_ICON}
                        style={{ ...this.muiStyles.searchIcon }}
                        onClick={this.showSearchModal}
                    ></TuneOutlinedIcon>
                    {!advancedSearchKey &&
                    viewport !== VIEWPORT_MOBILE &&
                    selectedSearchType === DATATEST.SEARCH_TYPE_EXPANDED
                        ? searchTypeToggle
                        : ""}
                    {advancedSearchKey && viewport !== VIEWPORT_MOBILE && advancedSearchToggle}
                </div>

                {viewport !== VIEWPORT_MOBILE && viewport !== VIEWPORT_PHABLET && type === "catalog" && (
                    <Toggle
                        data-test={DATATEST.CATALOG.CATALOG_VIEW_NOTES}
                        label={formatMessage("View Notes")}
                        labelPosition="right"
                        className="viewNotes"
                        toggled={viewNotes}
                        onToggle={onViewNotesToggle}
                        style={this.muiStyles.toggle}
                        labelStyle={this.muiStyles.toggleLabel}
                    />
                )}
                <ViewPicker viewport={viewport} value={view} onChange={onViewChange} choices={this.viewChoices} />
                {type === "item" && (
                    <Sort
                        viewport={viewport}
                        value={`${sortBy}.${sortOrder}`}
                        choices={this.sortChoices}
                        onChange={onSortChange}
                        style={this.muiStyles.sort}
                    />
                )}

                <SearchModal
                    data-test={DATATEST.SEARCH_TYPE_MODAL_DIALOG_ID}
                    open={showSearchTypeModel}
                    onConfirm={this.handleSearchTypeModal}
                    onClose={this.closeSearchTypeModal}
                    handleRadioChange={this.handleRadioChange}
                    selectedSearchType={selectedSearchType}
                    brandId={brandId}
                />
            </div>
        );
    }
}

export { ItemsHeader };

export default ItemsHeader;
